import React, { ReactElement, ReactNode, useCallback, useMemo } from "react";
import { useAuth } from "@context/auth";

export const PERMISSIONS = {
  corporate: {
    read: "usermgmt.corporates.read",
  },
  editions: {
    virtual: {
      create: "editions.virtual.create",
      retrieve: "editions.virtual.retrieve",
      update: "editions.virtual.update",
      enrollment: "editions.virtual.enrollment",
    },
    physical: {
      create: "editions.physical.create",
      retrieve: "editions.physical.retrieve",
      update: "editions.physical.update",
      enrollment: "editions.physical.enrollment",
    },
  },
  registry: {
    read: "catalogue.virtualregister.read",
    download: "catalogue.virtualregister.download",
    upload: "catalogue.virtualregister.upload",
    write: "catalogue.virtualregister.write",
  },
  enrollment: {
    read: "learning.enrollment.read",
    create: "learning.enrollment.create.write",
    createMassive: "learning.enrollment.create.massive.write",
    update: "learning.enrollment.update.enrollment.write",
    // Permesso reale del backend (come V1, route attributes): la vecchia
    // stringa "enrollment.attributes.read" non veniva mai assegnata.
    attributes: "fe.enrollment.attributes.read",
    reminder: "learning.enrollment.create.reminder.write",
    reminderMassive: "learning.enrollment.create.reminder.massive.write",
    statusMonitor: "learning.enrollment.status.monitor",
    statusUpdate: "learning.enrollment.status.update",
    statusMassiveUpdate: "learning.enrollment.status.massive.update",
    trackerRead: "learning.enrollment.tracker.read",
    trackerWrite: "learning.enrollment.tracker.write",
  },
  certificate: {
    read: "fe.certificate.template.read",
    // Download dei certificati emessi agli iscritti (come V1).
    issuedRead: "certificate.issued.read",
  },
  users: {
    read: "usermgmt.users.read",
    add: "usermgmt.users.add",
    loginAs: "usermgmt.loginas",
  },
  mfa: {
    totpReset: "usermgmt.mfa.admin.totp.reset",
  },
  topics: {
    read: "usermgmt.topics.read",
  },
  initiativeCategories: {
    read: "usermgmt.initiatives.categories.read",
  },
  customFields: {
    read: "usermgmt.corporates.custom_fields.read",
    write: "usermgmt.corporates.custom_fields.write",
  },
  visibilityCones: {
    read: "usermgmt.corporates.visibility_cones.read",
    write: "usermgmt.corporates.visibility_cones.write",
  },
  onboarding: {
    write: "usermgmt.onboarding.signup.write",
  },
  media: {
    read: "mediamanager.media.read",
    write: "mediamanager.media.write",
    userProfileWrite: "mediamanager.media.userprofile.write",
  },
  badge: {
    read: "fe.badge.template.read",
    write: "fe.badge.template.write",
  },
  catalog: {
    read: "fe.corporate.catalog",
    write: "learning_catalogue.learning_object.write",
    learningObjectRead: "learning_catalogue.learning_object.read",
    associateInitiatives:
      "learning_catalogue.learning_object.associate.initiatives",
    associateCategories:
      "learning_catalogue.learning_object.associate.initiatives.categories",
    associateTopics: "learning_catalogue.learning_object.associate.topics",
    corporateVisibility:
      "learning_catalogue.learning_object.corporate.visibility",
  },
  groups: {
    dynamic: {
      read: "usermgmt.corporates.dynamic_groups.read",
      write: "usermgmt.corporates.dynamic_groups.write",
    },
    static: {
      read: "usermgmt.corporates.static_groups.read",
      write: "usermgmt.corporates.static_groups.write",
    },
  },
} as const;

// "Il mio spazio": ogni riquadro richiede gli stessi permessi della pagina
export const canSeeTrainingOffers = (permissions: string[] = []): boolean =>
  [
    PERMISSIONS.corporate.read,
    PERMISSIONS.catalog.read,
    PERMISSIONS.catalog.corporateVisibility,
  ].every((p) => permissions.includes(p));

export const canSeeUsersGroups = (permissions: string[] = []): boolean =>
  permissions.includes(PERMISSIONS.corporate.read) &&
  [
    PERMISSIONS.users.read,
    PERMISSIONS.groups.static.read,
    PERMISSIONS.groups.dynamic.read,
  ].some((p) => permissions.includes(p));

export const canSeeMySpace = (permissions: string[] = []): boolean =>
  canSeeTrainingOffers(permissions) || canSeeUsersGroups(permissions);

export type EditionKindPerm = "virtual" | "physical";
export type EditionActionPerm = "create" | "retrieve" | "update" | "enrollment";

export const editionPerm = (
  kind: EditionKindPerm,
  action: EditionActionPerm,
): string => PERMISSIONS.editions[kind][action];

export const usePermissions = () => {
  const { user } = useAuth();

  const granted = useMemo(
    () => new Set<string>(user?.permissions ?? []),
    [user?.permissions],
  );

  const can = useCallback(
    (permission?: string | string[] | null): boolean => {
      if (!permission) return true;
      const list = Array.isArray(permission) ? permission : [permission];
      return list.every((p) => granted.has(p));
    },
    [granted],
  );

  const canAny = useCallback(
    (permission: string[]): boolean => permission.some((p) => granted.has(p)),
    [granted],
  );

  return { can, canAny };
};

interface PermissionGateProps {
  permission?: string | string[] | null;
  anyOf?: string[];
  mode?: "hide" | "disable";
  children: ReactElement;
  fallback?: ReactNode;
}

export const PermissionGate: React.FC<PermissionGateProps> = ({
  permission,
  anyOf,
  mode = "hide",
  children,
  fallback = null,
}) => {
  const { can, canAny } = usePermissions();
  const allowed = anyOf ? canAny(anyOf) : can(permission);

  if (allowed) return children;
  if (mode === "disable") {
    return React.cloneElement(children, { disabled: true });
  }
  return <>{fallback}</>;
};
